import {
  Box,
  Center,
  chakra,
  Flex,
  Grid,
  GridItem,
  Skeleton,
  Spacer,
  Stack,
  useDisclosure,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import { UsePaginationFn } from '@diamond/shared/context';
import { QueryWrapperNext } from '@diamond/shared/data-access';
import {
  Product,
  ProductCategory,
  ProductSubCategory,
} from '@diamond/shared/types';
import {
  Breadcrumbs,
  Button,
  Divider,
  Modal,
  Pagination,
  Text,
} from '@diamond/shared/ui';
import { TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { SEONext } from '@diamond/sol-buyer/seo-next';
import {
  useAuthStore,
  useBrowseProductData,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { query as urlQuery } from 'urlcat';
import shallow from 'zustand/shallow';

import { Image } from '../common';
import { BuyerLayout } from '../layouts/BuyerLayout';
import { BuyerPageLoader } from '../layouts/BuyerPageLoader';
import BrowseProductFilter from './BrowseProductFilter';
import {
  FilterDivisi,
  FilterStorage,
  FilterSubCategory,
  getProductFilterRoute,
  ItemSortBy,
  MappedSubCategoryData,
  MappedSubCategoryLv3Data,
  ProductFilters,
} from './filters';

type BaseBrowseProductListProps = {
  products: Product[];
  isLoadingProducts: boolean;
  totalProduct: number;
  totalPageProduct: number;
  refetchProduct: () => void;
  filters: ProductFilters;
  pagination: UsePaginationFn;
  queriesEnabled?: boolean;
  isParentLoading?: boolean;
  isFetchingProducts?: boolean;
  renderCardProduct: (
    product: Product,
    price: number,
    idx: number
  ) => ReactNode;
};

type WithBreadcrumbs = {
  mode: 'breadcrumbs';
  baseBreadcrumbLinks: Array<{ title: string; link: string }>;
};

type NoBreadcrumbs = {
  mode: 'title';
  title?: string;
};

type BrowseProductListProps = BaseBrowseProductListProps &
  (WithBreadcrumbs | NoBreadcrumbs);

export function BrowseProductList(props: BrowseProductListProps) {
  const {
    filters,
    products: productDatas,
    isLoadingProducts,
    totalProduct,
    totalPageProduct,
    refetchProduct: refetch,
    pagination,
    mode,
    renderCardProduct,
    queriesEnabled = true,
    isParentLoading = false,
    isFetchingProducts = false,
  } = props;
  const { access_token: token } = useAuthStore();
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const router = useRouter();
  const form = useForm();

  const [basePath] = router.asPath.split('?');

  const [links, setLink] = useState<{ title: string }[]>([]);
  const [filterValue, setFilterValue] = useState({
    category_lv1: '',
    category_lv2: '',
    category_lv3: '',
  });

  const queryProductCategory = QueryWrapperNext('/product/category', token);
  const queryProductSubCategory = QueryWrapperNext(
    '/product/sub-category',
    token
  );

  const { divisionData, storagesData, sortsData, isLoadingProductFilter } =
    useBrowseProductData();

  const { data: productCategory, isLoading: isLoadingProductCategory } =
    queryProductCategory.useQueryData<ProductCategory>(
      'ProductCategory',
      {
        divisi_code: filters.divisi_code,
        storage_code: filters.storage_code,
        search_query: filters.search_query,
        warehouse: selectedShipToId,
        product_id_substitute: filters.product_id,
      },
      {
        enabled: queriesEnabled,
        prefetchNextPage: false,
      }
    );

  const totalProductCount = productCategory.reduce(
    (sum, item) => sum + item.product_count,
    0
  );

  const categoryDatas: Array<ProductCategory> = [
    {
      id: '',
      name: 'Semua',
      product_count: totalProductCount,
      icon: '',
      created_at: 0,
    },
    ...productCategory,
  ];

  useEffect(() => {
    if (filters.search_query === '') {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search_query]);

  const { data: subCategory } =
    queryProductSubCategory.useQueryGetById<ProductSubCategory>(
      queriesEnabled ? filters.category_lv1 : '',
      'ProductSubCategoryDesktop',
      {
        divisi_code: filters.divisi_code,
        storage_code: filters.storage_code,
        search_query: filters.search_query,
        warehouse: selectedShipToId,
        product_id_substitute: filters.product_id,
      },
      false
    );

  const subCategoryData =
    subCategory?.data.reduce<MappedSubCategoryData[]>((acc, item) => {
      if (item.product_count > 0) {
        acc.push({
          label: `${item.name} (${item.product_count})`,
          value: item.category_lv2,
          name: item.name,
          product_count: item.product_count,
          third_category: item.third_category,
        });
      }
      return acc;
    }, []) || [];

  const subCategoryLv3 = subCategoryData?.filter(
    (val) => val.value === filters.category_lv2
  );

  const subCategoryLv3Data =
    subCategoryLv3?.reduce<MappedSubCategoryLv3Data[]>((acc, cat2) => {
      cat2.third_category.reduce((cat2Acc, cat3) => {
        if (cat3.product_count > 0) {
          cat2Acc.push({
            label: `${cat3.name} (${cat3.product_count})`,
            name: cat3.name,
            value: cat3.category_lv3,
            product_count: cat3.product_count,
          });
        }
        return cat2Acc;
      }, acc);
      return acc;
    }, []) || [];

  const handleFilter = (
    category: string,
    categoryLv2: string,
    categoryLv3: string
  ) => {
    setFilterValue({
      category_lv1: category,
      category_lv2: categoryLv2,
      category_lv3: categoryLv3,
    });

    const route = getProductFilterRoute(basePath, router.query, {
      cat1: category,
      cat2: categoryLv2,
      cat3: categoryLv3,
      page: 1,
    });
    const urlParams = urlQuery(route.query);

    router.push(`${route.pathname}?${urlParams}`, undefined, {
      shallow: true,
    });

    refetch();
    onClose();
  };

  const { nextPage, previousPage, lastPage, firstPage } = pagination;

  useEffect(() => {
    if (filters.category_lv1) {
      setFilterValue((prev) => ({
        ...prev,
        category_lv1: filters.category_lv1,
      }));
    }
  }, [filters.category_lv1]);

  useEffect(() => {
    if (mode === 'breadcrumbs') {
      if (filterValue) {
        const links = props.baseBreadcrumbLinks;
        categoryDatas.forEach((val) => {
          if (val.id === filters.category_lv1 && links.length === 1) {
            if (val.name === 'Semua') {
              links.push({ title: 'Pencarian Semua Produk', link: '#' });
            } else {
              links.push({ title: val.name, link: '#' });
            }
          }
        });
        subCategoryData?.forEach((val) => {
          if (val.value === filters.category_lv2 && links.length === 2) {
            links.push({ title: val.name, link: '#' });
          }
        });
        subCategoryLv3Data?.forEach((val) => {
          if (val.value === filters.category_lv3 && links.length === 3) {
            links.push({ title: val?.name, link: '#' });
          }
        });
        setLink(links);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValue,
    filters.category_lv1,
    filters.category_lv2,
    filters.category_lv3,
  ]);

  if (
    isLoadingProductCategory ||
    isLoadingProductFilter ||
    isLoadingProducts ||
    isParentLoading
  ) {
    return <BuyerPageLoader />;
  }

  return (
    <BuyerLayout>
      <SEONext title={TRACKING_PAGES.PRODUCT_LIST} />
      <FormProvider {...form}>
        <Box mb="12" fontSize={{ base: '12px', md: '16px' }}>
          {mode === 'breadcrumbs' ? <Breadcrumbs links={links} /> : null}
          {mode === 'title' ? (
            <Text fontSize={{ base: '14px', md: '24px' }}>
              <chakra.span fontWeight="bold">Produk Serupa</chakra.span>
              <chakra.span ml="3">{props.title}</chakra.span>
            </Text>
          ) : null}
          <Divider my="4" mt="6" />
          <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
            <Box w={{ base: 'full', lg: '20%' }}>
              <Text
                fontWeight="semibold"
                fontSize="22px"
                mb="4"
                display={{ base: 'none', lg: 'flex' }}
              >
                Filter
              </Text>
              <Stack
                spacing={{ base: 1, lg: 4 }}
                direction={{ base: 'row', lg: 'column' }}
                w="full"
              >
                {isLoadingProductFilter || isLoadingProductCategory ? (
                  <>
                    <Skeleton height="140px" borderRadius="md" />
                    <Skeleton height="300px" borderRadius="md" />
                    <Skeleton height="180px" borderRadius="md" />
                  </>
                ) : (
                  <>
                    <FilterDivisi divisionData={divisionData} />
                    <FilterStorage storagesData={storagesData} />
                    <Box display={{ base: 'flex', lg: 'none' }} w="150px">
                      <Stack>
                        <Text
                          as="label"
                          fontSize={{ base: '12px', md: '16px' }}
                          fontWeight="semibold"
                        >
                          Urutkan
                        </Text>
                        <ItemSortBy sortsData={sortsData} />
                      </Stack>
                    </Box>
                    <FilterSubCategory
                      categoryDatas={categoryDatas}
                      subCategoryData={subCategoryData}
                      subCategoryLv3Data={subCategoryLv3Data}
                    />
                  </>
                )}
              </Stack>
            </Box>
            <Box w={{ base: 'full', lg: '80%' }}>
              {mode === 'breadcrumbs' ? (
                <Text fontWeight="semibold" fontSize="22px">
                  Daftar Produk
                </Text>
              ) : null}
              {isFetchingProducts ? (
                <>
                  <Flex display={{ base: 'flex', lg: 'none' }}>
                    <Skeleton>
                      <Box mt="3">
                        <Text as="span" color="blue" fontWeight="semibold">
                          {totalProduct.toLocaleString('id-ID')}
                        </Text>
                        <Text
                          as="span"
                          color="gray.75"
                          fontWeight="semibold"
                          ml={1}
                        >
                          Hasil
                        </Text>
                      </Box>
                    </Skeleton>
                    <Spacer />
                    <Stack direction="row" spacing="2" align="center">
                      <Button size="sm" onClick={onOpen}>
                        Filter
                      </Button>
                    </Stack>
                  </Flex>
                  {mode === 'breadcrumbs' ? <Divider my="3" /> : null}
                  <Flex
                    my={mode === 'breadcrumbs' ? '10' : '2'}
                    display={{ base: 'none', lg: 'flex' }}
                  >
                    <Skeleton>
                      <Box>
                        <Text as="span" color="blue" fontWeight="semibold">
                          {totalProduct.toLocaleString('id-ID')}
                        </Text>
                        <Text
                          as="span"
                          color="gray.75"
                          fontWeight="semibold"
                          ml={1}
                        >
                          Hasil
                        </Text>
                      </Box>
                    </Skeleton>
                    <Spacer />
                    <Stack direction="row" spacing="2" align="center">
                      <Text>Urutkan</Text>
                      <ItemSortBy sortsData={sortsData} />
                    </Stack>
                  </Flex>
                  <Box overflow="auto" my={{ base: 4, lg: 10 }} flex="1">
                    <Grid
                      templateColumns={{
                        base: 'repeat(2, 1fr)',
                        lg: 'repeat(5, 1fr)',
                      }}
                      gap={{ base: 2, lg: 4 }}
                      w="full"
                    >
                      {Array(25)
                        .fill(null)
                        .map((_, i) => (
                          <Skeleton height="380px" borderRadius="md" key={i} />
                        ))}
                    </Grid>
                  </Box>
                  <Center>
                    <Skeleton>
                      <Pagination
                        currentPage={+filters.page}
                        totalPage={totalPageProduct}
                        nextPage={nextPage}
                        firstPage={firstPage}
                        lastPage={lastPage}
                        previousPage={previousPage}
                      />
                    </Skeleton>
                  </Center>
                </>
              ) : productDatas.length > 0 ? (
                <>
                  <Flex display={{ base: 'flex', lg: 'none' }}>
                    <Box mt="3">
                      <Text as="span" color="blue" fontWeight="semibold">
                        {totalProduct.toLocaleString('id-ID')}
                      </Text>
                      <Text
                        as="span"
                        color="gray.75"
                        fontWeight="semibold"
                        ml={1}
                      >
                        Hasil
                      </Text>
                    </Box>
                    <Spacer />
                    <Stack direction="row" spacing="2" align="center">
                      <Button size="sm" onClick={onOpen}>
                        Filter
                      </Button>
                    </Stack>
                  </Flex>
                  {mode === 'breadcrumbs' ? <Divider my="3" /> : null}
                  <Flex
                    my={mode === 'breadcrumbs' ? '10' : '2'}
                    display={{ base: 'none', lg: 'flex' }}
                  >
                    <Box>
                      <Text as="span" color="blue" fontWeight="semibold">
                        {totalProduct.toLocaleString('id-ID')}
                      </Text>
                      <Text
                        as="span"
                        color="gray.75"
                        fontWeight="semibold"
                        ml={1}
                      >
                        Hasil
                      </Text>
                    </Box>
                    <Spacer />
                    <Stack direction="row" spacing="2" align="center">
                      <Text>Urutkan</Text>
                      <ItemSortBy sortsData={sortsData} />
                    </Stack>
                  </Flex>
                  <Box overflow="auto" my={{ base: 4, lg: 10 }} flex="1">
                    <Grid
                      templateColumns={{
                        base: 'repeat(2, 1fr)',
                        lg: 'repeat(5, 1fr)',
                      }}
                      gap={{ base: 2, lg: 4 }}
                      w="full"
                    >
                      {productDatas.map((val, idx) => {
                        const price =
                          val.prices.length > 0 ? val.prices[0].price : 0;
                        return (
                          <GridItem key={idx}>
                            {renderCardProduct(val, price, idx)}
                          </GridItem>
                        );
                      })}
                    </Grid>
                  </Box>
                  <Center>
                    <Pagination
                      currentPage={+filters.page}
                      totalPage={totalPageProduct}
                      nextPage={nextPage}
                      firstPage={firstPage}
                      lastPage={lastPage}
                      previousPage={previousPage}
                    />
                  </Center>
                </>
              ) : (
                <Center py={16}>
                  <VStack spacing={4}>
                    <Image
                      src="/images/empty-product-illustration.svg"
                      alt="Sukanda Onelink"
                      width={isMobile ? 200 : 400}
                      height={isMobile ? 200 : 400}
                    />
                    <Text
                      fontWeight="semibold"
                      fontSize={{ base: 'sm', md: '2xl' }}
                      textAlign="center"
                    >
                      Produk saat ini tidak ditemukan
                    </Text>
                  </VStack>
                </Center>
              )}
            </Box>
          </Stack>
          <Modal
            title="Filter"
            name="Filter Produk"
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onClose}
            hideAction
          >
            <BrowseProductFilter
              onSubmit={handleFilter}
              productCategory={productCategory}
              totalProductCount={totalProductCount}
              filterParamByCategory1={filters.category_lv1}
              filterParamByCategory2={filters.category_lv2}
              filterParamByCategory3={filters.category_lv3}
              keyword={filters.search_query}
              warehouseId={selectedShipToId}
              divisiCode={filters.divisi_code}
              storageCode={filters.storage_code}
            />
          </Modal>
        </Box>
      </FormProvider>
    </BuyerLayout>
  );
}
